import { useCallback, useEffect, useRef, useState } from 'react';
import { isLegalBasis } from '@assist/contracts';
import { renderProblem } from '@assist/i18n';
import {
  AlertBanner,
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  ProgressBar,
  Select,
} from '@assist/ui';
import {
  endpoints,
  ApiError,
  type TenantAgreementResponse,
  type TenantAuditResponse,
  type TenantIdentity,
} from '../state/api.js';
import { useSession } from '../state/session.js';

/**
 * IDENTITAS ORGANISASI — Figma `216:2030` (EN) / `216:2294` (ID), keadaan logo
 * `221:2314` / `221:2515`.
 *
 * Diterjemahkan dari frame itu, elemen per elemen, dan bukan dari uraian: dua kolom
 * 760 / 352, panel identitas di atas isi Pasal 51 yang sudah ada, dan EMPAT keadaan
 * medan logo yang seluruhnya digambar — belum ada, sedang mengunggah, ditolak,
 * berhasil. Penanda `data-figma` di bawah adalah kontraknya
 * (`docs/kontrak-elemen-figma.json`), dan gerbangnya memeriksa DUA ARAH.
 */

const SHA256_UNAVAILABLE = 'SHA256_UNAVAILABLE';

async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  if (!globalThis.crypto?.subtle) throw new Error(SHA256_UNAVAILABLE);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Sama dengan `MIME_PER_PURPOSE.TENANT_LOGO` di kernel, dan sengaja lebih sempit dari enum global. */
const TIPE_LOGO = ['image/png', 'image/webp'] as const;
const BATAS_LOGO_BYTE = 1_048_576;

/**
 * Daftar zona waktu — SAMA dengan `ZONA_WAKTU` di `modules/M15/src/routes/identity.ts`.
 *
 * Ia disalin dan bukan diimpor karena konsol tidak boleh mengimpor modul server (lint
 * `assist/no-cross-module-call`); yang menahan perbedaannya adalah kontrak: `enum` medan
 * `timezone` di `api/openapi.yaml` dibangkitkan dari daftar server, dan sebuah nilai yang
 * hanya ada di sini ditolak 422 oleh rutenya dengan kode `TIMEZONE_NOT_SUPPORTED` —
 * terlihat, bukan diam.
 */
const ZONA_WAKTU = ['Asia/Jakarta', 'Asia/Makassar', 'Asia/Jayapura', 'UTC'] as const;

/**
 * Layar Organisasi Anda — M15 · rute `/settings/organisation` · izin MGR.
 *
 * Tiga hal yang seorang manajer organisasi pelanggan perlu dapat melihat sendiri,
 * tanpa meminta apa pun kepada PQM:
 *
 *   1. PERJANJIAN pemrosesan datanya — teksnya, versinya, siapa yang menyetujuinya,
 *      jabatannya, dan kapan. Sebuah perjanjian yang hanya ada di kotak surel orang
 *      yang sudah pindah kerja bukan perjanjian yang dapat dipegang.
 *   2. DAFTAR PEMROSESAN — apa yang benar-benar diproses, dirakit dari manifest modul.
 *      Tautannya menuju halaman publik yang sama, dan itu disengaja: yang dibaca
 *      pelanggan setelah menandatangani harus benar-benar sama dengan yang dibacanya
 *      sebelum menandatangani.
 *   3. JEJAK AUDIT ORGANISASINYA SENDIRI — **butir P8**, dan ini yang paling penting.
 *
 * KENAPA BUTIR P8 ADA DI LAYAR INI
 * --------------------------------
 * Sampai rilis ini, `app.can_read_audit()` berbunyi `app.is_platform()` saja: satu-
 * satunya yang dapat membaca jejak akses adalah PQM sendiri. Hak Pengendali memeriksa
 * pemrosesan yang dilakukan Prosesornya (Pasal 51) tidak dapat dipenuhi begitu, dan
 * pertanyaan yang pasti diajukan setiap klien besar dalam uji tuntas — "bagaimana kami
 * tahu konsultan Anda tidak melihat data kami di luar jam kerja?" — tidak punya
 * jawaban di dalam produk.
 *
 * Sekarang ada. Yang membatasi barisnya adalah kebijakan RESTRICTIVE
 * `audit_tenant_scope`, bukan kueri: manajer memperoleh jejak organisasinya, dan tidak
 * memperoleh baris platform maupun baris organisasi lain — bahkan bila seseorang kelak
 * menulis ulang kuerinya.
 */
export function Organisation() {
  const { t, locale, translator } = useSession();
  const [agreement, setAgreement] = useState<TenantAgreementResponse | null>(null);
  const [audit, setAudit] = useState<TenantAuditResponse | null>(null);
  const [identity, setIdentity] = useState<TenantIdentity | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  /** Sunting-an yang BELUM disimpan. `null` berarti "sama dengan yang di server". */
  const [nama, setNama] = useState<string | null>(null);
  const [aksen, setAksen] = useState<string | null>(null);
  const [bahasa, setBahasa] = useState<string | null>(null);
  const [zona, setZona] = useState<string | null>(null);
  const [menyimpan, setMenyimpan] = useState(false);
  const [tersimpan, setTersimpan] = useState(false);
  const [galatSimpan, setGalatSimpan] = useState<ApiError | null>(null);

  /**
   * KEADAAN UNGGAH LOGO — empat, dan keempatnya digambar Figma.
   *
   * `null` berarti tidak sedang mengunggah; keadaan "berhasil" dan "belum ada" datang
   * dari `identity.logo_state`, bukan dari sini. Penolakan TIDAK disimpan di server —
   * ia peristiwa di layar ini — dan karena itu ia keadaan lokal, bukan keadaan baris.
   */
  const [unggah, setUnggah] = useState<'uploading' | 'checking' | null>(null);
  const [tolak, setTolak] = useState<string | null>(null);
  const berkasInput = useRef<HTMLInputElement | null>(null);

  const load = useCallback(() => {
    setFailed(null);
    void Promise.all([
      endpoints.tenantAgreement(),
      endpoints.tenantAudit(50),
      endpoints.tenantIdentity(),
    ])
      .then(([a, j, i]) => {
        setAgreement(a);
        setAudit(j);
        setIdentity(i);
        setNama(null);
        setAksen(null);
        setBahasa(null);
        setZona(null);
      })
      .catch((err: unknown) =>
        setFailed(err instanceof ApiError ? err.problem.code : 'INTERNAL'),
      );
  }, []);

  useEffect(load, [load]);

  /**
   * UNGGAH LOGO — tiga permintaan, dan berkasnya tidak melewati API (ADR-8).
   *
   * Tipe dan ukuran diperiksa DI SINI JUGA, dan itu bukan pengulangan yang sia-sia:
   * pemeriksaan di peramban menghemat satu unggahan yang pasti gagal dan memberi orang
   * kalimat yang benar dalam satu detik alih-alih satu menit. Yang MENAHAN tetap tiga
   * lapis di server — enum Zod, `inspectUpload()` yang membaca byte-nya, dan CHECK
   * `file_purpose_valid` — dan sebuah SVG yang dinamai `.png` lolos dari pemeriksaan
   * `file.type` di sini dengan mudah, lalu mati di lapis kedua.
   */
  async function unggahLogo(file: File) {
    setTolak(null);
    setGalatSimpan(null);
    if (!(TIPE_LOGO as readonly string[]).includes(file.type)) {
      setTolak(t('identity.logo.wrongType'));
      return;
    }
    if (file.size > BATAS_LOGO_BYTE) {
      setTolak(t('identity.logo.tooLarge'));
      return;
    }
    setUnggah('uploading');
    try {
      const intent = await endpoints.uploadIntent({
        purpose: 'TENANT_LOGO',
        mime_type: file.type as 'image/png',
        byte_size: file.size,
        filename: file.name,
      });
      const bytes = await file.arrayBuffer();
      const checksum = await sha256Hex(bytes);
      const put = await fetch(intent.upload_url, {
        method: 'PUT',
        headers: { 'content-type': file.type },
        body: bytes,
      });
      if (!put.ok) {
        setTolak(t('identity.logo.wrongType'));
        return;
      }
      setUnggah('checking');
      await endpoints.commitFile(intent.file_id, {
        byte_size: file.size,
        checksum_sha256: checksum,
      });
      const baru = await endpoints.tenantIdentityUpdate({ logo_mark_file_id: intent.file_id });
      setIdentity(baru);
    } catch (err) {
      if (err instanceof ApiError) setTolak(renderProblem(translator, err.problem));
      else setTolak(t('identity.logo.wrongType'));
    } finally {
      setUnggah(null);
      if (berkasInput.current) berkasInput.current.value = '';
    }
  }

  async function simpan() {
    setGalatSimpan(null);
    setTersimpan(false);
    setMenyimpan(true);
    try {
      const baru = await endpoints.tenantIdentityUpdate({
        ...(nama !== null ? { name: nama } : {}),
        ...(aksen !== null ? { accent_hex: aksen } : {}),
        ...(bahasa !== null ? { default_locale: bahasa as 'id-ID' } : {}),
        ...(zona !== null ? { timezone: zona } : {}),
      });
      setIdentity(baru);
      setNama(null);
      setAksen(null);
      setBahasa(null);
      setZona(null);
      setTersimpan(true);
    } catch (err) {
      if (err instanceof ApiError) setGalatSimpan(err);
    } finally {
      setMenyimpan(false);
    }
  }

  if (failed) {
    return (
      <ErrorState
        title={t('state.error.title')}
        body={t('state.error.body')}
        code={failed}
        retryLabel={t('app.retry')}
        onRetry={load}
      />
    );
  }
  if (!agreement || !audit || !identity) return <LoadingState rows={6} label={t('app.loading')} />;

  const accepted = agreement.accepted;
  const namaKini = nama ?? identity.name ?? '';
  const aksenKini = aksen ?? identity.accent_hex ?? '';
  const bahasaKini = bahasa ?? identity.default_locale;
  const zonaKini = zona ?? identity.timezone;
  const adaSuntingan =
    nama !== null || aksen !== null || bahasa !== null || zona !== null;

  return (
    <section>
      <h1>{t('identity.title')}</h1>
      <p>{t('identity.subtitle')}</p>

      {/*
        PANEL LOGO — Figma `217:2160`. Empat keadaan, dan tidak satu pun dari keempatnya
        adalah halaman galat: belum ada logo bukan kesalahan siapa pun, unggahan yang
        sedang berjalan belum selesai, penolakan menyebut sebabnya dan TIDAK menyentuh
        logo yang sudah ada, dan berhasil menyebut apa yang terbukti dari byte-nya.
      */}
      <div className="identity">
        <div className="identity__col">
      <section className="card" data-figma="organisation.identitas">
      <h2 className="card__title">{t('identity.logo.heading')}</h2>
      <div className="assist-identity__logo" data-figma="organisation.logo">
        {identity.logo_state === 'READY' && identity.logo_mark_url ? (
          <>
            <img
              className="assist-identity__logo-preview"
              src={identity.logo_mark_url}
              alt={t('identity.logo.label')}
              width={64}
              height={64}
            />
            <p className="assist-privacy__meta">
              <Badge tone="success">{t('identity.logo.verified')}</Badge>{' '}
              {t('identity.logo.verifiedDetail', {
                size: formatBytes(identity.logo_byte_size, locale),
                width: identity.logo_width_px ?? 0,
                height: identity.logo_height_px ?? 0,
              })}
            </p>
          </>
        ) : identity.logo_state === 'VERIFYING' ? (
          <AlertBanner
            kind="information"
            signalWord={t('signal.information')}
            title={t('identity.logo.verifying')}
          >
            {t('identity.logo.verifyingBody')}
          </AlertBanner>
        ) : (
          <p className="assist-privacy__meta">{t('identity.logo.none')}</p>
        )}

        <p className="assist-privacy__meta">{t('identity.logo.hint')}</p>

        {unggah ? (
          <ProgressBar
            value={unggah === 'uploading' ? 40 : 80}
            label={t('identity.logo.uploading')}
            tone="default"
          />
        ) : null}

        {/*
          PENOLAKAN — `221:2790`. Ia BANNER dan bukan tooltip: alasan penolakan harus
          terbaca tanpa interaksi, dan kalimatnya menyatakan bahwa logo yang sudah ada
          TIDAK berubah. Penolakan yang hanya berbunyi "gagal" menyuruh orang mencoba
          berkas yang sama sekali lagi.
        */}
        {tolak ? (
          <div data-figma="organisation.logo.tolak">
            <AlertBanner
              kind="danger"
              signalWord={t('signal.danger')}
              title={t('identity.logo.rejected')}
              onDismiss={() => setTolak(null)}
            >
              {tolak}
            </AlertBanner>
          </div>
        ) : null}

        <input
          ref={berkasInput}
          type="file"
          accept={TIPE_LOGO.join(',')}
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void unggahLogo(f);
          }}
        />
        <Button
          variant="secondary"
          disabled={unggah !== null}
          onClick={() => berkasInput.current?.click()}
        >
          {identity.logo_state === 'READY'
            ? t('identity.logo.replace')
            : tolak
              ? t('identity.logo.chooseAnother')
              : t('identity.logo.choose')}
        </Button>
        {identity.logo_state !== 'NONE' ? (
          <Button
            variant="text"
            disabled={unggah !== null}
            onClick={() => {
              void endpoints
                .tenantIdentityUpdate({ hapus_logo: true })
                .then(setIdentity)
                .catch((err: unknown) => {
                  if (err instanceof ApiError) setTolak(renderProblem(translator, err.problem));
                });
            }}
          >
            {t('identity.logo.remove')}
          </Button>
        ) : null}
        <p className="assist-privacy__meta">{t('identity.logo.whereShown')}</p>
      </div>
      </section>

      <section className="card" data-figma="organisation.namaTampilan">
      <h2 className="card__title">{t('identity.name.heading')}</h2>
      <Input
        label={t('identity.name.heading')}
        value={namaKini}
        hint={t('identity.name.hint')}
        onChange={(e) => setNama(e.target.value)}
      />
      </section>
        </div>

        <div className="identity__col">
      <section className="card" data-figma="organisation.bahasaZona">
      <h2 className="card__title">{t('identity.locale.heading')}</h2>
      <Select
        label={t('identity.locale.label')}
        value={bahasaKini}
        options={[
          { value: 'id-ID', label: t('app.language.id') },
          { value: 'en-US', label: t('app.language.en') },
        ]}
        onChange={(e) => setBahasa(e.target.value)}
      />
      <Select
        label={t('identity.timezone.label')}
        value={zonaKini}
        hint={t('identity.locale.hint')}
        options={ZONA_WAKTU.map((z) => ({ value: z, label: z }))}
        onChange={(e) => setZona(e.target.value)}
      />

      {/*
        WARNA AKSEN — `218:2186`. Lencana kontras `218:2195` BUKAN hiasan: server
        menghitung rasio terhadap `surface/00` dan `surface/02` dan menolak 422 beserta
        angkanya. Yang ditampilkan di sini hanyalah apa yang SUDAH tersimpan, jadi ia
        tidak dapat menjanjikan hasil pemeriksaan yang belum dijalankan.
      */}
      </section>

      <section className="card" data-figma="organisation.aksen">
      <h2 className="card__title">{t('identity.accent.heading')}</h2>
      <Input
        label={t('identity.accent.heading')}
        value={aksenKini}
        hint={t('identity.accent.hint')}
        onChange={(e) => setAksen(e.target.value)}
      />
      </section>

      <section className="card" data-figma="organisation.miliPqm">
      <h2 className="card__title">{t('identity.pqm.heading')}</h2>
      <ul className="assist-consent-list">
        <li className="assist-consent-list__item">
          <strong>{t('identity.pqm.legalName')}</strong>
          <span>{identity.legal_name ?? identity.name}</span>
        </li>
        <li className="assist-consent-list__item">
          <strong>{t('identity.pqm.address')}</strong>
          <span>{identity.slug}</span>
        </li>
        <li className="assist-consent-list__item">
          <strong>{t('identity.pqm.plan')}</strong>
          <span>{identity.plan_code}</span>
        </li>
      </ul>
      <p className="assist-privacy__meta">{t('identity.pqm.hint')}</p>
      </section>
        </div>
      </div>

      {galatSimpan ? (
        <AlertBanner
          kind="danger"
          signalWord={t('signal.danger')}
          title={renderProblem(translator, galatSimpan.problem)}
        />
      ) : null}
      {tersimpan ? (
        <AlertBanner
          kind="success"
          signalWord={t('signal.success')}
          title={t('identity.saved')}
          onDismiss={() => setTersimpan(false)}
        />
      ) : null}
      <Button variant="primary" disabled={!adaSuntingan || menyimpan} onClick={() => void simpan()}>
        {t('identity.save')}
      </Button>
      <Button
        variant="text"
        disabled={!adaSuntingan || menyimpan}
        onClick={() => {
          setNama(null);
          setAksen(null);
          setBahasa(null);
          setZona(null);
        }}
      >
        {t('identity.discard')}
      </Button>

      <h2>{t('organisation.title')}</h2>
      <p>{t('organisation.subtitle')}</p>

      <h2>{t('agreement.title')}</h2>
      {accepted ? (
        <p className="assist-privacy__meta">
          {t('agreement.acceptedBy', {
            name: accepted.accepted_by_name,
            title: accepted.accepted_by_title,
            date: formatDate(accepted.accepted_at, locale),
          })}
          {' · '}
          {t('agreement.version', { version: accepted.agreement_version })}
        </p>
      ) : (
        /*
         * BELUM DISETUJUI dinyatakan sebagai keadaan, bukan sebagai galat, dan
         * kalimatnya menyebut siapa yang harus bertindak. Sebuah halaman galat kepada
         * orang yang tidak dapat melakukan apa pun tentangnya hanya membuatnya
         * mencoba lagi.
         */
        <AlertBanner
          kind="warning"
          signalWord={t('signal.warning')}
          title={t('agreement.pending.title')}
        >
          {t('agreement.pending.body')}
        </AlertBanner>
      )}

      {agreement.agreement ? (
        <article className="assist-privacy">
          <p className="assist-privacy__meta">
            {t('agreement.version', { version: agreement.agreement.version })}
          </p>
          <div className="assist-privacy__body">{agreement.agreement.body}</div>
          <p className="assist-privacy__meta">{agreement.agreement.contact}</p>
        </article>
      ) : null}

      <Button variant="text" onClick={() => window.location.assign('/processing-register')}>
        {t('register.title')}
      </Button>

      <h2>{t('organisation.auditHeading')}</h2>
      <p>{t('organisation.auditNote')}</p>
      {audit.items.length === 0 ? (
        <EmptyState title={t('organisation.auditEmpty')} body={t('organisation.auditNote')} />
      ) : (
        <>
          {/*
            KODE PERISTIWANYA DITAMPILKAN APA ADANYA, dan itu keputusan yang perlu
            ditulis karena ia tampak seperti kunci i18n yang lupa diterjemahkan.
            `audit_log.event_code` adalah teks bebas — tidak ada enum, tidak ada CHECK
            constraint, dan karena itu tidak ada kosakata tertutup yang dapat
            dienumerasi gerbang teks antarmuka. Menulis daftar terjemahan dengan tangan
            akan benar hari ini dan diam-diam kehilangan kode-kode baru besok, dan
            sebuah jejak audit yang menampilkan "(tidak diketahui)" untuk peristiwa
            terbaru adalah jejak yang tidak dapat dipakai memeriksa apa pun.
            Nilai inilah yang juga muncul di ekspor jejak audit, jadi yang dibaca
            petugas kepatuhan di layar sama dengan yang dibacanya di berkas.
          */}
          <p className="assist-privacy__meta">{t('organisation.auditCodeNote')}</p>
          <ul className="assist-consent-list">
            {audit.items.map((row) => (
              <li className="assist-consent-list__item" key={row.id}>
                <strong>{row.event_code}</strong>
                <span>{formatDate(row.occurred_at, locale)}</span>
                <span>
                  {row.actor_name ?? actorLabel(t, row.actor_kind)}
                  {row.processing_basis ? ` · ${basisLabel(t, row.processing_basis)}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

type Terjemah = (key: string, params?: Record<string, string | number>) => string;

/**
 * Jenis pelaku ditulis sebagai CABANG STATIS, bukan sebagai kunci yang dirakit.
 *
 * `actor_kind` memang kosakata tertutup (enum lima nilai di basis data), tetapi
 * merakit `t(\`x.\${row.actor_kind}\`)` memaksa gerbang teks antarmuka mengenumerasi
 * enum basis data dari TypeScript — dan gerbang yang harus menebak dari mana sebuah
 * prefiks datang adalah gerbang yang akan dilonggarkan orang berikutnya. Lima cabang
 * ini terlihat lebih panjang dan jauh lebih mudah diperiksa.
 *
 * `CONSULTANT` dibedakan dari `PLATFORM` dengan sengaja: bagi pelanggan, "konsultan
 * PQM yang ditugaskan kepada kami" dan "administrator platform PQM" adalah dua orang
 * dengan dua alasan berbeda untuk menyentuh datanya, dan menyatukannya menghapus
 * justru pembedaan yang ia ingin periksa.
 */
function actorLabel(t: Terjemah, kind: string): string {
  switch (kind) {
    case 'TENANT':
      return t('organisation.actor.TENANT');
    case 'PLATFORM':
      return t('organisation.actor.PLATFORM');
    case 'CONSULTANT':
      return t('organisation.actor.CONSULTANT');
    case 'SYSTEM':
      return t('organisation.actor.SYSTEM');
    default:
      return t('organisation.actor.ANONYMOUS');
  }
}

/**
 * Dasar pemrosesan — kunci katalognya DITURUNKAN, bukan disebutkan satu per satu.
 *
 * TELAAH KODE 6, TEMUAN M-7 (bentuk kedua, ditemukan gerbangnya). Bentuk sebelumnya
 * adalah `switch` berisi keenam nama kosakata sebagai literal, dan cabang `default:`-nya
 * mengembalikan `privacy.basis.LEGITIMATE_INTEREST` untuk nilai apa pun yang tidak
 * dikenalnya. Akibatnya bukan sekadar salinan: sebuah dasar hukum BARU yang ditambahkan
 * ke `privacy-vocabulary.json` — yang kalimatnya sudah wajib ada di kedua bahasa karena
 * gerbang teks antarmuka mengenumerasinya dari kosakata itu — akan tampil kepada pembaca
 * sebagai "kepentingan sah", DIAM-DIAM dan tanpa satu pun gerbang memerah. Keterangan
 * dasar hukum yang salah lebih berbahaya daripada keterangan yang hilang.
 *
 * `isLegalBasis()` membaca daftarnya dari kosakata yang sama (`@assist/contracts` tidak
 * menyalin satu pun nilainya, dan gerbang `cek-dasar-pemrosesan.py` pemeriksaan 6
 * memeriksanya). Nilai yang TIDAK dikenal karena itu tidak lagi ditebak — ia ditampilkan
 * apa adanya sebagai kode, yang jelek dan benar, alih-alih cantik dan salah.
 */
function basisLabel(t: Terjemah, basis: string): string {
  return isLegalBasis(basis) ? t(`privacy.basis.${basis}`) : basis;
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );
  } catch {
    return iso.slice(0, 16);
  }
}

/**
 * Ukuran berkas dalam kilobyte, dibulatkan.
 *
 * Bukan `Intl.NumberFormat` dengan `unit: 'kilobyte'`: dukungannya tidak merata dan
 * kegagalannya diam. Angka di sini muncul di kalimat "diperiksa dari byte-nya", dan
 * kalimat itu kehilangan seluruh maksudnya bila angkanya kosong.
 */
function formatBytes(bytes: number | null, locale: string): string {
  if (bytes === null) return '—';
  const kb = Math.max(1, Math.round(bytes / 1024));
  try {
    return `${new Intl.NumberFormat(locale).format(kb)} KB`;
  } catch {
    return `${kb} KB`;
  }
}
